import axios from 'axios';
import { ACCESS_TOKEN_KEY } from '../constants';

interface ErrorResponseBody {
    message?: string;
}

const EXPORT_REQUEST_TIMEOUT_MILLISECONDS = 120_000;

function getBackendRestBaseUrl(): string {
    const backendGraphQLUrl = import.meta.env.VITE_APOLLO_URI;
    if (!backendGraphQLUrl) {
        throw new Error(
            'VITE_APOLLO_URI is not defined in environment variables.',
        );
    }
    const normalizedBackendAddress = backendGraphQLUrl.replace(/\/+$/, '');
    return normalizedBackendAddress.endsWith('/graphql')
        ? normalizedBackendAddress.slice(0, -'/graphql'.length)
        : normalizedBackendAddress;
}

function joinUrl(base: string, exportPath: string): string {
    const trimmedBase = base.replace(/\/+$/, '');
    const path = exportPath.startsWith('/') ? exportPath : `/${exportPath}`;
    return `${trimmedBase}${path}`;
}

function parseFilenameFromContentDisposition(
    header: unknown,
): string | undefined {
    if (typeof header !== 'string' || !header) {
        return undefined;
    }

    const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        try {
            return decodeURIComponent(utfMatch[1]);
        } catch (error: unknown) {
            if (!(error instanceof URIError)) {
                throw error;
            }
        }
    }

    const quotedMatch = header.match(/filename="([^"]+)"/i);
    if (quotedMatch?.[1]) {
        return quotedMatch[1];
    }

    const plainMatch = header.match(/filename=([^;]+)/i);
    return plainMatch?.[1]?.trim();
}

async function parseJsonMessageFromBlob(
    blob: Blob,
): Promise<string | undefined> {
    const text = await blob.text();
    try {
        const parsed = JSON.parse(text) as ErrorResponseBody;
        return typeof parsed.message === 'string' ? parsed.message : undefined;
    } catch (parseError: unknown) {
        if (parseError instanceof SyntaxError) {
            return undefined;
        }
        throw parseError;
    }
}

async function ensureBlobSuccess(
    blob: Blob,
    status: number,
    fallbackMessage = 'Export failed',
): Promise<Blob> {
    if (status >= 200 && status < 300) {
        return blob;
    }
    const parsedMessage = await parseJsonMessageFromBlob(blob);
    const message = parsedMessage ?? `${fallbackMessage} (${status})`;
    throw new Error(message);
}

export async function getExportErrorMessage(error: unknown): Promise<string> {
    if (axios.isAxiosError(error)) {
        const data: unknown = error.response?.data;
        if (data instanceof Blob) {
            const parsedMessage = await parseJsonMessageFromBlob(data);
            return parsedMessage ?? error.message ?? 'Export failed';
        }
        if (
            data &&
            typeof data === 'object' &&
            'message' in data &&
            typeof (data as { message: unknown }).message === 'string'
        ) {
            return (data as { message: string }).message;
        }
        return error.message ?? 'Export failed';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Export failed';
}

export class ExcelExportHttpClient {
    static async postBlob(exportPath: string, body: unknown): Promise<Blob> {
        const response = await this.requestBlob('post', exportPath, body);
        return ensureBlobSuccess(response.data, response.status);
    }

    static async getBlob(
        downloadPath: string,
    ): Promise<{ blob: Blob; fileName?: string }> {
        const response = await this.requestBlob('get', downloadPath);
        const blob = await ensureBlobSuccess(
            response.data,
            response.status,
            'Download failed',
        );
        const contentDisposition = response.headers['content-disposition'];
        return {
            blob,
            fileName: parseFilenameFromContentDisposition(contentDisposition),
        };
    }

    private static async requestBlob(
        method: 'get' | 'post',
        path: string,
        body?: unknown,
    ) {
        const baseUrl = getBackendRestBaseUrl();
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (!accessToken) {
            throw new Error(
                'Your session token is missing. Please log in again.',
            );
        }
        const url = joinUrl(baseUrl, path);
        return axios.request<Blob>({
            url,
            method,
            data: body,
            headers: {
                ...(method === 'post'
                    ? { 'Content-Type': 'application/json' }
                    : {}),
                Authorization: `Bearer ${accessToken}`,
            },
            responseType: 'blob',
            timeout: EXPORT_REQUEST_TIMEOUT_MILLISECONDS,
            validateStatus: () => true,
        });
    }
}

export const excelExportHttpClient = ExcelExportHttpClient;
